import * as React from "react";
import { Accumulator } from "object-accumulator";
import Deepmerge from "deepmerge";
import { merge as _merge } from "lodash";

import {
  UserData,
  GenerateDataFormModel,
  DataLevel,
} from "../func/generateUsers";
import { Segment, Header, Grid, Button, Form } from "semantic-ui-react";
import { Formik, Field, FieldProps } from "formik";
import TestingStatus, {
  TestStatus,
  TestStatusEnum,
} from "../components/TestingStatus";

enum TestName {
  "Accumulator" = "Accumulator",
  "ObjectAssign" = "ObjectAssign",
  "DeepMerge" = "DeepMerge",
  "Lodash" = "Lodash",
}

export interface TestConfig {
  retryTimes: number;
  extract: boolean;
}

interface Target {
  DisplayName: string;
  Description: string;
  canDeepMerge?: boolean;
  merge: (items: any[]) => any;
  extract: (items: any[], key: string) => any;
}

const Targets: { [key in TestName]: Target } = {
  [TestName.DeepMerge]: {
    DisplayName: "deepmerge",
    Description: "Very popular package for merging objects",
    merge: items => Deepmerge.all(items),
    extract: (items, key) => (Deepmerge.all(items) as any)[key],
    canDeepMerge: true,
  },
  [TestName.ObjectAssign]: {
    DisplayName: "Object.assign with spread operator",
    Description:
      "Vanilla Javascript function for shallow merging objects, cannot test 2-level merge",
    merge: items => items.reduce((acc, i) => Object.assign(acc, i), {}),
    extract: (items, key) =>
      items.reduce((acc, i) => Object.assign(acc, i), {})[key],
  },
  [TestName.Accumulator]: {
    DisplayName: "Accumulator",
    Description: "THIS package",
    merge: items => Accumulator.from(items).merge(),
    extract: (items, key) => Accumulator.from(items).extract(key),
    canDeepMerge: true,
  },
  [TestName.Lodash]: {
    DisplayName: "Lodash _.merge()",
    Description: "Very popular utility package for Javascript",
    merge: items => items.reduce((acc, i) => _merge(acc, i), {}),
    extract: (items, key) => items.reduce((acc, i) => _merge(acc, i), {})[key],
    canDeepMerge: true,
  },
};

const TestTarget = React.forwardRef(
  (
    props: {
      testData: any[];
      target: Target;
      generatorConfig: GenerateDataFormModel;
      testingConfig?: TestConfig;
    },
    ref,
  ) => {
    const [testStatus, setTestStatus] = React.useState<TestStatus>(
      props.generatorConfig.level === DataLevel.TwoLevel &&
        !props.target.canDeepMerge
        ? { status: TestStatusEnum.NotAvailable }
        : { status: TestStatusEnum.NotTested },
    );

    const runTest = async () => {
      const { target, generatorConfig, testingConfig } = props;
      if (!testingConfig) {
        return;
      }
      if (
        generatorConfig.level === DataLevel.TwoLevel &&
        !target.canDeepMerge
      ) {
        return;
      }
      setTestStatus({ status: TestStatusEnum.Testing, progress: 0 });
      await new Promise<undefined>(resolve => setTimeout(resolve, 100));
      try {
        if (testingConfig.extract) {
          const t1 = performance.now();
          let result: any;
          for (
            let count = 0;
            count < props.testingConfig!.retryTimes;
            count++
          ) {
            setTestStatus({
              status: TestStatusEnum.Testing,
              progress: count + 1,
            });
            await new Promise<undefined>(resolve => setTimeout(resolve, 100));
            result = await new Promise<any>(resolve =>
              resolve(props.target.extract(props.testData, "email")),
            );
          }
          const t2 = performance.now();
          const status: TestStatus = {
            status: TestStatusEnum.Done,
            performance: (t2 - t1) / props.testingConfig!.retryTimes,
            output: result,
          };
          setTestStatus(status);
          return status;
        } else {
          const t1 = performance.now();
          let result: any;
          for (
            let count = 0;
            count < props.testingConfig!.retryTimes;
            count++
          ) {
            setTestStatus({
              status: TestStatusEnum.Testing,
              progress: count + 1,
            });
            await new Promise<undefined>(resolve => setTimeout(resolve, 100));
            result = await new Promise<any>(resolve =>
              resolve(props.target.merge(props.testData)),
            );
          }
          const t2 = performance.now();
          const status: TestStatus = {
            status: TestStatusEnum.Done,
            performance: (t2 - t1) / props.testingConfig!.retryTimes,
            output: JSON.stringify(result, undefined, "  "),
          };
          setTestStatus(status);
          return status;
        }
      } catch (e) {
        const status: TestStatus = {
          status: TestStatusEnum.Error,
          message: e.name,
        };
        setTestStatus(status);
        return status;
      }
    };

    React.useImperativeHandle(ref, () => ({
      runTest,
    }));

    return (
      <Segment vertical>
        <Grid>
          <Grid.Row>
            <Grid.Column computer="12" tablet="10" mobile="10">
              <Header as="h4">
                {props.target.DisplayName}
                <Header.Subheader>{props.target.Description}</Header.Subheader>
              </Header>
            </Grid.Column>
            <Grid.Column computer="4" tablet="6" mobile="6" textAlign="right">
              <TestingStatus
                result={testStatus}
                testConfig={props.testingConfig}
              />
            </Grid.Column>
            {testStatus.status === TestStatusEnum.Done ? (
              <Grid.Column width="16">
                <Header as="h5">Output</Header>
                <Segment inverted>
                  <code>{testStatus.output}</code>
                </Segment>
              </Grid.Column>
            ) : null}
          </Grid.Row>
        </Grid>
      </Segment>
    );
  },
);

export default function TestingView(props: {
  users: UserData;
  generatorConfig: GenerateDataFormModel;
}) {
  const [testingConfig, setTestingConfig] = React.useState<TestConfig>();
  // const [testStatus, setTestStatus] = React.useState<
  //   { [key in TestName]?: TestStatus }
  // >({});

  const accumulatorTestTargetRef = React.useRef<typeof TestTarget>();
  const deepmergeTestTargetRef = React.useRef<typeof TestTarget>();
  const assignTestTargetRef = React.useRef<typeof TestTarget>();
  const lodashTestTargetRef = React.useRef<typeof TestTarget>();

  return (
    <>
      <Formik<TestConfig>
        initialValues={{
          retryTimes: 5,
          extract: true,
        }}
        onSubmit={async form => {
          setTestingConfig(form);
          await (accumulatorTestTargetRef.current! as any).runTest();
          await (deepmergeTestTargetRef.current! as any).runTest();
          await (lodashTestTargetRef.current! as any).runTest();
          await (assignTestTargetRef.current! as any).runTest();
        }}
      >
        {({ submitForm }) => (
          <Form>
            <Grid>
              <Grid.Row>
                <Grid.Column computer="5" tablet="8" mobile="16">
                  <Form.Field>
                    <label>Mode</label>
                    <Field
                      name="extract"
                      render={({ form, field }: FieldProps<TestConfig>) => (
                        <Button.Group>
                          <Button
                            active={field.value}
                            onClick={() => form.setFieldValue("extract", true)}
                          >
                            Extract
                          </Button>
                          <Button
                            active={!field.value}
                            onClick={() => form.setFieldValue("extract", false)}
                          >
                            Merge
                          </Button>
                        </Button.Group>
                      )}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column computer="8" tablet="16" mobile="16">
                  <Form.Field>
                    <label>Times</label>
                    <Field
                      name="retryTimes"
                      render={({ form, field }: FieldProps<TestConfig>) => (
                        <Form.Input type="text" value={field.value} action>
                          <input disabled />
                          <Button
                            onClick={() =>
                              form.setFieldValue(
                                "retryTimes",
                                Math.max(field.value - 1, 1),
                              )
                            }
                          >
                            -1
                          </Button>
                          <Button
                            onClick={() =>
                              form.setFieldValue(
                                "retryTimes",
                                Math.min(field.value + 1, 10),
                              )
                            }
                          >
                            +1
                          </Button>
                        </Form.Input>
                      )}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column computer="3" tablet="16" mobile="16">
                  <Form.Field>
                    <label>-</label>
                    <Button primary fluid onClick={submitForm}>
                      Run
                    </Button>
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        )}
      </Formik>
      <TestTarget
        ref={accumulatorTestTargetRef}
        testData={props.users}
        testingConfig={testingConfig}
        target={Targets.Accumulator}
        generatorConfig={props.generatorConfig}
      />
      <TestTarget
        ref={deepmergeTestTargetRef}
        testData={props.users}
        testingConfig={testingConfig}
        target={Targets.DeepMerge}
        generatorConfig={props.generatorConfig}
      />
      <TestTarget
        ref={lodashTestTargetRef}
        testData={props.users}
        testingConfig={testingConfig}
        target={Targets.Lodash}
        generatorConfig={props.generatorConfig}
      />
      <TestTarget
        ref={assignTestTargetRef}
        testData={props.users}
        testingConfig={testingConfig}
        target={Targets.ObjectAssign}
        generatorConfig={props.generatorConfig}
      />
    </>
  );
}
